import { getData } from 'country-list';
import { useState } from 'react';
import Select from 'react-select';

// Get countries in the format needed for react-select
const countries = getData()
  .map(country => ({
    value: country.code,
    label: country.name,
    // Put UK first
    sortOrder: country.code === 'GB' ? -1 : 0,
  }))
  .sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.label.localeCompare(b.label);
  });

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    background: '#1A1A1A', // hdlcc-darkGray
    borderColor: state.isFocused ? '#3B82F6' : '#333333', // hdlcc-lightBlue : hdlcc-gray
    boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
    '&:hover': {
      borderColor: '#3B82F6',
    },
  }),
  menu: base => ({
    ...base,
    background: '#1A1A1A', // hdlcc-darkGray
    border: '1px solid #333333', // hdlcc-gray
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? '#333333' : '#1A1A1A', // hdlcc-gray : hdlcc-darkGray
    color: 'white',
    '&:active': {
      background: '#3B82F6', // hdlcc-lightBlue
    },
  }),
  singleValue: base => ({
    ...base,
    color: 'white',
  }),
  input: base => ({
    ...base,
    color: 'white',
  }),
  placeholder: base => ({
    ...base,
    color: '#9CA3AF', // gray-400
  }),
};

function AddressInput({
  value = {},
  onChange,
  onBlur,
  errors = {},
  touched = {},
  disabled = false,
}) {
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find(c => c.value === (value.country || 'GB'))
  );

  const handleCountryChange = option => {
    setSelectedCountry(option);
    onChange({
      ...value,
      country: option.value,
      // Clear postcode when country changes as format might be different
      postcode: '',
    });
  };

  const handleChange = e => {
    const { name, value: fieldValue } = e.target;
    // For postcode validation, we need to pass both postcode and country
    if (name === 'postcode') {
      onChange({
        ...value,
        postcode: fieldValue,
        country: selectedCountry.value,
      });
    } else {
      onChange({
        ...value,
        [name]: fieldValue,
      });
    }
  };

  const inputClasses = fieldName =>
    `w-full p-2 border rounded bg-hdlcc-darkGray text-white ${
      touched[fieldName] && errors[fieldName] ? 'border-red-500' : 'border-hdlcc-gray'
    } focus:border-hdlcc-lightBlue focus:outline-none [&:-webkit-autofill]:!bg-hdlcc-darkGray [&:-webkit-autofill]:!text-white [&:-webkit-autofill]:![box-shadow:0_0_0_50px_rgb(26,26,26)_inset] [&:-webkit-autofill]:![transition-delay:0s] [&:-webkit-autofill]:![-webkit-text-fill-color:white]`;

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="address1" className="block text-white mb-2">
          Address Line 1
        </label>
        <input
          id="address1"
          type="text"
          name="address1"
          value={value.address1 || ''}
          onChange={handleChange}
          onBlur={onBlur}
          className={inputClasses('address1')}
          autoComplete="address-line1"
          disabled={disabled}
        />
        {touched.address1 && errors.address1 && (
          <p className="text-red-500 text-sm mt-1">{errors.address1}</p>
        )}
      </div>

      <div>
        <label htmlFor="address2" className="block text-white mb-2">
          Address Line 2
        </label>
        <input
          id="address2"
          type="text"
          name="address2"
          value={value.address2 || ''}
          onChange={handleChange}
          onBlur={onBlur}
          className={inputClasses('address2')}
          autoComplete="address-line2"
          disabled={disabled}
        />
      </div>

      <div>
        <label htmlFor="country" className="block text-white mb-2">
          Country
        </label>
        <Select
          inputId="country"
          value={selectedCountry}
          onChange={handleCountryChange}
          options={countries}
          className="basic-single"
          classNamePrefix="select"
          isSearchable={true}
          name="country"
          placeholder="Select country..."
          styles={customSelectStyles}
          isDisabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="city" className="block text-white mb-2">
            City
          </label>
          <input
            id="city"
            type="text"
            name="city"
            value={value.city || ''}
            onChange={handleChange}
            onBlur={onBlur}
            className={inputClasses('city')}
            autoComplete="address-level2"
            disabled={disabled}
          />
          {touched.city && errors.city && (
            <p className="text-red-500 text-sm mt-1">{errors.city}</p>
          )}
        </div>

        <div>
          <label htmlFor="region" className="block text-white mb-2">
            {selectedCountry?.value === 'US'
              ? 'State'
              : selectedCountry?.value === 'GB'
                ? 'County'
                : 'Region'}
            {selectedCountry?.value !== 'US' && ' (optional)'}
          </label>
          <input
            id="region"
            type="text"
            name="region"
            value={value.region || ''}
            onChange={handleChange}
            onBlur={onBlur}
            className={inputClasses('region')}
            autoComplete="address-level1"
            disabled={disabled}
          />
          {touched.region && errors.region && (
            <p className="text-red-500 text-sm mt-1">{errors.region}</p>
          )}
        </div>
      </div>

      <div className="max-w-xs">
        <label htmlFor="postcode" className="block text-white mb-2">
          {selectedCountry?.value === 'US' ? 'ZIP Code' : 'Postal Code'}
        </label>
        <input
          id="postcode"
          type="text"
          name="postcode"
          value={value.postcode || ''}
          onChange={handleChange}
          onBlur={onBlur}
          className={inputClasses('postcode')}
          autoComplete="postal-code"
          disabled={disabled}
        />
        {touched.postcode && errors.postcode && (
          <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>
        )}
      </div>
    </div>
  );
}

export default AddressInput;
